import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Select, Store } from "@ngxs/store";
import { IUser } from "@zonar-ui/auth/lib/models/user.model";
import { Observable } from "rxjs";
import { filter } from "rxjs/operators";
import { AppState } from "src/app/app.state";
import { PaginatorComponent } from "src/app/components/paginator/paginator.component";
import { translateAndFormat } from "src/app/i18next";
import { formatDate } from "src/app/i18next/formatDate";
import { QueryStrings } from "src/app/models/emitter-events.models";
import { LanguageDictionaryService } from "src/app/services/language-dictionary/language-dictionary.service";
import { getZonarOtherUUID } from "src/utils/getZonarOtherUUID/getZonarOtherUUID";
import { isDefined } from "src/utils/isDefined/isDefined";
import { ClosedDefectViewModel, OpenDefectTableViewModel } from "../../../models/open-defect-table.models";
import { SeverityIconService } from "../../../services/severity-icon.service";
import { ClosedDefects } from "../asset-details.component";
import { AssetState, SetClosedWithComments } from "../state/asset-details.state";

export interface ColumnGroup {
	column: string;
	displayName: string;
	sortable: boolean;
}

@Component({
	selector: "app-closed-ignored",
	templateUrl: "./closed-ignored.component.html",
	styleUrls: ["./closed-ignored.component.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class ClosedIgnoredComponent implements OnInit, OnChanges {
  @Input() newClosedDefects: ClosedDefects;
  @Input() totalClosedDefects = 0;
  @Input() currentClosedDefectQuery: QueryStrings;
  @Output() closedDefectPaginate = new EventEmitter<QueryStrings>();

	@Select(AppState.getSelectedCompanyId) selectedCompanyId$: Observable<string>;
	@Select(AppState.selectUser) user$: Observable<IUser>;
	@Select(AssetState.getClosedWithComments) closedWithComments$: Observable<ClosedDefectViewModel[]>;

  public displayedColumns: string[] = [
    "severity",
    "repairedDate",
    "inspection",
    "zone",
    "component",
    "condition",
    "mechanic",
    "notes",
  ];
  public displayedColumnGroup: ColumnGroup[] = [
    {
      column: "severity",
      displayName: translateAndFormat("severity", "uppercase"),
      sortable: true,
    },
    {
      column: "repairedDate",
      displayName: translateAndFormat("date", "uppercase"),
      sortable: true,
    },
    {
      column: "inspection",
      displayName: translateAndFormat("inspection", "uppercase"),
      sortable: true,
    },
    {
      column: "zone",
      displayName: translateAndFormat("zone", "uppercase"),
      sortable: true,
    },
    {
      column: "component",
      displayName: translateAndFormat("component", "uppercase"),
      sortable: true,
    },
    {
      column: "condition",
      displayName: translateAndFormat("condition", "uppercase"),
      sortable: true,
    },
    {
      column: "mechanic",
      displayName: translateAndFormat("mechanic", "uppercase"),
      sortable: false,
    },
    {
      column: "notes",
      displayName: translateAndFormat("notes / resolution", "uppercase"),
      sortable: false,
    },
  ];
  public closedWithComments: ClosedDefectViewModel[] = [];
  public companyId: string;
  public dataSource: MatTableDataSource<ClosedDefectViewModel>;
  public noDefects = false;
  public pageIndex = 0;
  public pageSize = 5;
  public pageSizeOptions = [5, 10, 20];
  public repairDefectString: string = translateAndFormat("please repair an open defect", "capitalize");
  public user: IUser;
  sortQueryStringValues: Record<string, string> = {
    severity: "severity",
    repairedDate: "repairDate",
    inspection: "inspectionName",
    zone: "zone",
    component: "component",
    condition: "condition",
  };

	@ViewChild(MatSort, { static: true }) sort: MatSort;
	@ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
	@ViewChild("appPaginator") paginatorComponent!: PaginatorComponent;

	constructor(
		private store: Store,
		private severityIconService: SeverityIconService,
		private languageDictionaryService: LanguageDictionaryService,
	) {}

	ngOnInit() {
		this.selectedCompanyId$.pipe(filter(isDefined)).subscribe(companyId => {
			this.companyId = companyId;
		});

		this.closedWithComments$.pipe(filter(isDefined)).subscribe((closedWithComments: ClosedDefectViewModel[]) => {
			this.dataSource = new MatTableDataSource(closedWithComments);
			this.closedWithComments = closedWithComments;

			if (closedWithComments.length === 0) {
				this.noDefects = true;
			}
		});

		this.user$.pipe(filter(isDefined)).subscribe((user: IUser) => {
			this.user = user;
		});

		if (this.dataSource) {
			this.dataSource.sort = this.sort;
			this.dataSource.paginator = this.paginator;
		}
	}

	// this will catch the input changes (closed defects) from parent
	// SimpleChanges is a hashmap of inputs (like newClosedDefects) that have a SimpleChange type that hold prev and cur vals
	// Only want to know if the entire object changed (new defects were closed) and use the new input val to update state
	ngOnChanges(changes: SimpleChanges) {
		if (changes.newClosedDefects && this.newClosedDefects !== null) {
			this.newClosedDefects.closedDefects.forEach((closedDefect: OpenDefectTableViewModel) => {
				const newClosedDefect: ClosedDefectViewModel = {
					component: closedDefect.componentLabel,
					condition: closedDefect.conditionLabel,
					inspection: closedDefect.inspectionType,
					inspectionId: closedDefect.inspectionId,
					mechanic: this.user ? `${this.user.lastName}, ${this.user.firstName}` : "—",
					notes: this.buildRepairNotes(this.newClosedDefects),
					repairedDate: formatDate(new Date()),
					severity: closedDefect.severity,
					zone: closedDefect.zoneLabel,
				};

				this.closedWithComments = [...this.closedWithComments, newClosedDefect];
			});

			this.store.dispatch(new SetClosedWithComments(this.closedWithComments));

			this.noDefects = false;
			this.dataSource.paginator = this.paginator;
			this.dataSource.sort = this.sort;
		}
	}

	// if resolution (cleaned, adjusted, etc) anything but other, put resolution in notes column
	// if resolution is other, put the comment in notes instead
	// if no resolution, defect ignored, put "Repair not needed" in notes column
	public buildRepairNotes(defects: ClosedDefects): string {
		if (defects.resolution) {
			return defects.resolution ===
				this.languageDictionaryService.getTranslations({
					languageKey: getZonarOtherUUID(),
					languageLabel: "Other",
				})
				? defects.comment
				: defects.resolution;
		} else {
			return translateAndFormat("repair not needed", "capitalize");
		}
	}

	public getSeverityIcon(severity: number): string {
		return this.severityIconService.getSeverityIcon(severity);
	}

  updateTable(currentQueryStrings: QueryStrings): void {
    this.closedDefectPaginate.emit(currentQueryStrings);
  }

	onQueryChange(query: URLSearchParams): void {
		this.pageIndex = parseInt(query.get("page"));
		this.pageSize = parseInt(query.get("perPage"));

    this.updateTable({
      ...this.currentClosedDefectQuery,
      page: this.pageIndex.toString(),
      perPage: this.pageSize.toString(),
    });
  }

  onMatSortChange(event: Sort) {
    this.updateTable({
      ...this.currentClosedDefectQuery,
      page: "1",
      sort: `${this.sortQueryStringValues[event.active]}`,
      sortDir: event.direction,
    });
    this.pageIndex = 0;
    this.paginatorComponent?.resetPageIndex();
  }
}
